/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { dashboardsSaveLayout } from "../funcs/dashboardsSaveLayout.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { Dashboard } from "../models/components/dashboard.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  SaveDashboardLayoutRequest,
  SaveDashboardLayoutSecurity,
} from "../models/operations/savedashboardlayout.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type SaveDashboardLayoutMutationVariables = {
  request: SaveDashboardLayoutRequest;
  security?: SaveDashboardLayoutSecurity | undefined;
  options?: RequestOptions;
};

export type SaveDashboardLayoutMutationData = Dashboard;

export type SaveDashboardLayoutMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * saveDashboardLayout dashboards
 *
 * @remarks
 * Move and resize a dashboard's cards. Each card names an existing placement and where it now sits; cards not listed stay as they are, so a layout saved from an older view cannot take off a card someone has just added. A card is added with addWidget and taken off with removeWidget. The grid is 12 columns wide, and each chart type has a minimum size. Layout autosaves, so the last save wins.
 */
export function useSaveDashboardLayoutMutation(
  options?: MutationHookOptions<
    SaveDashboardLayoutMutationData,
    SaveDashboardLayoutMutationError,
    SaveDashboardLayoutMutationVariables
  >,
): UseMutationResult<
  SaveDashboardLayoutMutationData,
  SaveDashboardLayoutMutationError,
  SaveDashboardLayoutMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildSaveDashboardLayoutMutation(client, options),
    ...options,
  });
}

export function mutationKeySaveDashboardLayout(): MutationKey {
  return ["@gram/client", "dashboards", "saveLayout"];
}

export function buildSaveDashboardLayoutMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: SaveDashboardLayoutMutationVariables,
  ) => Promise<SaveDashboardLayoutMutationData>;
} {
  return {
    mutationKey: mutationKeySaveDashboardLayout(),
    mutationFn: function saveDashboardLayoutMutationFn({
      request,
      security,
      options,
    }): Promise<SaveDashboardLayoutMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(dashboardsSaveLayout(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
